import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bell, Calendar, ChevronRight, Compass, ClipboardCheck, Heart, LayoutList, Target, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import ModuleTitleRHYTHM from "@/components/rhythm/ModuleTitleRHYTHM";
import { ScriptureVerse } from "@/components/patria/ScriptureVerse";
import AgendaQuickAdd from "@/components/rhythm/AgendaQuickAdd";

const HEALTH_CHIP = {
  green: "patria-chip-green",
  yellow: "patria-chip-terra",
  red: "patria-chip-terra",
};
const HEALTH_LABEL = { green: "On Cadence", yellow: "Slipping", red: "Cadence Red" };

function renderPairingCard(p, side, setPairings, user) {
  const isSupervisor = String(p.supervisor_id) === String(user?.id);
  const otherName = isSupervisor ? p.employee_name : p.supervisor_name;
  const relationLine = isSupervisor
    ? `Supervising ${p.employee_name}`
    : `With ${p.supervisor_name} · your supervisor`;
  return (
    <div
      key={p.id}
      className="patria-card p-5 hover:shadow-lg transition"
      data-testid={`rhythm-pairing-${p.id}`}
      data-side={side}
    >
      <Link
        to={`/rhythm/workspace/${p.id}`}
        className="block"
        data-testid={`rhythm-pairing-open-${p.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
              {p.effective_cadence.charAt(0).toUpperCase() + p.effective_cadence.slice(1)}
              {p.compass_override && " · COMPASS Override"}
            </p>
            <p className="font-serif text-xl text-forest-900 mt-1">{otherName}</p>
            <p className="text-xs text-charcoal-muted">{relationLine}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={HEALTH_CHIP[p.health]}>
              {p.health === "green" ? "✓" : <AlertTriangle className="h-3 w-3 mr-1" />}
              {HEALTH_LABEL[p.health]}
            </span>
            {p.unread_agenda_from_other > 0 && (
              <span
                className="patria-chip-terra text-[10px]"
                data-testid={`rhythm-unread-${p.id}`}
                title={`${p.unread_agenda_from_other} new agenda topic(s) from the other party`}
              >
                <Bell className="h-3 w-3 mr-1" strokeWidth={1.5} />
                {p.unread_agenda_from_other} New
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 text-xs text-charcoal-soft">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
            Next 1:1: {p.next_scheduled || "—"}
          </span>
          {p.compass_override && (
            <span className="inline-flex items-center gap-1 text-terracotta-600">
              <Compass className="h-3.5 w-3.5" strokeWidth={1.5} /> COMPASS Active
            </span>
          )}
        </div>
      </Link>
      <div className="mt-4">
        <AgendaQuickAdd
          pairingId={p.id}
          onAdded={() => setPairings((prev) => prev.map((x) => x.id === p.id
            ? { ...x, unread_agenda_from_other: x.unread_agenda_from_other }
            : x))}
          suggestions={false}
          compact
          placeholder="Add A Topic For Your Next 1:1…"
          testIdPrefix={`landing-quick-add-${p.id}`}
        />
      </div>
      <Link
        to={`/rhythm/workspace/${p.id}`}
        className="mt-3 inline-flex items-center gap-1 text-sm text-forest-700 font-medium"
        data-testid={`rhythm-pairing-open-link-${p.id}`}
      >
        Open Workspace <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
      </Link>
    </div>
  );
}


export default function RhythmLanding() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wwEligibility, setWwEligibility] = useState(null);
  const isPO = ["po_admin", "super_admin"].includes(user?.role);
  const isPastor = !!user?.is_campus_pastor;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rhythm/pairings");
        setPairings(data);
      } catch (err) {
        toast.error("Couldn't load your pairings right now.");
      } finally { setLoading(false); }
    })();
    api.get("/weekly-wins/eligibility")
      .then((r) => setWwEligibility(r.data))
      .catch(() => setWwEligibility({ can_submit: false, can_review: false }));
  }, []);

  const myPairings = pairings.filter((p) => String(p.employee_id) === String(user?.id));
  const teamPairings = pairings.filter((p) => String(p.supervisor_id) === String(user?.id));

  return (
    <div className="space-y-8" data-testid="rhythm-landing">
      <section className="patria-card p-8 relative overflow-hidden">
        <ModuleTitleRHYTHM tagline="Regular Huddles Yielding Trust, Health, And Momentum" />
        <h1 className="mt-3 text-4xl sm:text-5xl font-serif text-forest-900">
          Your Ongoing Conversations
        </h1>
        <p className="mt-3 text-charcoal-soft max-w-2xl">
          RHYTHM is the space between LIFT and COMPASS — the steady 1:1 cadence
          that keeps trust real. Warm agendas, honest notes, gentle follow-through.
        </p>
        <div className="mt-6">
          {settings?.verses_enabled && (
            <ScriptureVerse
              verse={{
                text: "As iron sharpens iron, so one person sharpens another.",
                attribution: "Proverbs 27:17",
              }}
            />
          )}
        </div>
      </section>

      {(wwEligibility?.can_submit || wwEligibility?.can_review) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="weekly-wins-cards">
          {wwEligibility?.can_submit && (
            <Link
              to="/rhythm/weekly-wins"
              className="patria-card p-5 hover:shadow-lg transition border-l-4 border-terracotta-500"
              data-testid="weekly-wins-submit-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
                    Weekly Wins · Campus Health Briefing
                  </p>
                  <p className="font-serif text-xl text-forest-900 mt-1">
                    Your {wwEligibility.campus_name || "Campus"} Report
                  </p>
                  <p className="text-xs text-charcoal-muted mt-1 max-w-md">
                    Warm, honest weekly snapshot from you as Campus Pastor.
                    Due Sundays. Autosaves as you go.
                  </p>
                </div>
                <ClipboardCheck className="h-6 w-6 text-terracotta-500 shrink-0" strokeWidth={1.5} />
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm text-forest-700 font-medium">
                Open This Week&rsquo;s Report <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </div>
            </Link>
          )}
          {wwEligibility?.can_review && (
            <Link
              to="/rhythm/weekly-wins/rollup"
              className="patria-card p-5 hover:shadow-lg transition border-l-4 border-forest-600"
              data-testid="weekly-wins-rollup-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-forest-700 font-medium">
                    Reviewer · Weekly Wins Roll-Up
                  </p>
                  <p className="font-serif text-xl text-forest-900 mt-1">
                    All Campuses This Week
                  </p>
                  <p className="text-xs text-charcoal-muted mt-1 max-w-md">
                    Warm cross-campus view of Service Pulse, Leader Pulse, hot
                    potatoes, and commitments. Historical timeline + PDF export.
                  </p>
                </div>
                <LayoutList className="h-6 w-6 text-forest-700 shrink-0" strokeWidth={1.5} />
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm text-forest-700 font-medium">
                Open Roll-Up <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </div>
            </Link>
          )}
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-3xl text-forest-900">My Pairings</h2>
          {(isPO || isPastor) && (
            <Link
              to="/rhythm/dashboard"
              className="patria-btn-secondary text-sm"
              data-testid="rhythm-dashboard-link"
            >
              <Users className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Oversight Dashboard
            </Link>
          )}
        </div>
        {loading && <p className="italic text-charcoal-muted">Loading your pairings…</p>}
        {!loading && pairings.length === 0 && (
          <div className="patria-card p-8 text-center">
            <p className="font-serif text-xl text-forest-900">No pairings yet.</p>
            <p className="text-sm text-charcoal-soft mt-1">
              {isPO
                ? "Head to Admin to set up your first supervisor–employee pairing."
                : "Once People Operations pairs you with a supervisor, this space will come alive."}
            </p>
            {isPO && (
              <Link to="/rhythm/admin" className="patria-btn-primary mt-4 inline-flex" data-testid="rhythm-go-admin">
                Set Up A Pairing <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
              </Link>
            )}
          </div>
        )}

        {/* Split for dual-hat: "My RHYTHM" (viewer is the employee) shows first
            so a manager who is also being managed sees THEIR OWN growth first. */}
        {!loading && myPairings.length > 0 && (
          <div className="mb-8" data-testid="rhythm-section-my-rhythm">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
                  My RHYTHM · Where I&rsquo;m The Employee
                </p>
                <h3 className="font-serif text-2xl text-forest-900 mt-1">Your Own Development</h3>
                <p className="text-xs text-charcoal-muted mt-1 max-w-xl">
                  Your 1:1 rhythm with your own manager. This is where you bring the topics you
                  want walked through — and where your growth plan lives.
                </p>
              </div>
              <Link
                to={`/rhythm/growth/${user?.id}`}
                className="patria-btn-ghost text-sm"
                data-testid="link-my-growth-plan"
              >
                <Target className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> My Growth Plan
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myPairings.map((p) => renderPairingCard(p, "my", setPairings, user))}
            </div>
          </div>
        )}

        {!loading && teamPairings.length > 0 && (
          <div data-testid="rhythm-section-team-rhythm">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-forest-700 font-medium">
                  My Team&rsquo;s RHYTHM · Where I&rsquo;m The Supervisor
                </p>
                <h3 className="font-serif text-2xl text-forest-900 mt-1">Shepherding My Team</h3>
                <p className="text-xs text-charcoal-muted mt-1 max-w-xl">
                  Your direct reports&rsquo; workspaces. Warmly finalize records, close action items,
                  and keep the cadence steady.
                </p>
              </div>
              {(isPO || isPastor || teamPairings.length > 0) && (
                <Link
                  to="/rhythm/dashboard"
                  className="patria-btn-ghost text-sm"
                  data-testid="link-team-dashboard"
                >
                  <Users className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Team Dashboard
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teamPairings.map((p) => renderPairingCard(p, "team", setPairings, user))}
            </div>
          </div>
        )}
      </section>

      {isPO && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/rhythm/admin" className="patria-card p-5 hover:shadow-lg transition" data-testid="rhythm-admin-link">
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">PO Admin</p>
            <p className="font-serif text-xl text-forest-900 mt-1">RHYTHM Admin</p>
            <p className="text-xs text-charcoal-muted mt-1">
              Manage pairings, cadences, seasons, competencies, question banks, notifications, and retention.
            </p>
          </Link>
          <Link to="/rhythm/dashboard" className="patria-card p-5 hover:shadow-lg transition" data-testid="rhythm-dashboard-link-2">
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">Oversight</p>
            <p className="font-serif text-xl text-forest-900 mt-1">RHYTHM Dashboard</p>
            <p className="text-xs text-charcoal-muted mt-1">
              Org-wide cadence health, open action items, goal status, and COMPASS/LIFT flags.
            </p>
          </Link>
        </section>
      )}
    </div>
  );
}
